/*
 * rheincool — Inhaltsverzeichnis mit Scroll-Spy (HANDOFF.md §8.1)
 * Baut die Einträge aus allen h2[id]/[.rc-article-h2] der Seite,
 * markiert den aktiven Abschnitt (blaue Linie), Desktop sticky in der
 * linken Spalte (CSS), mobil Akkordeon.
 */
class RcArticleToc extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-toc-list]');
    if (!this.list) return;

    // Überschriften ohne ID bekommen eine (aus dem Text)
    this.headings = Array.from(
      document.querySelectorAll('main h2.rc-article-h2, main .rte h2, main [data-toc-heading]')
    ).filter((h) => h.textContent.trim());

    this.headings.forEach((heading) => {
      if (!heading.id) {
        heading.id = heading.textContent
          .trim()
          .toLowerCase()
          .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c]))
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
      }
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.className = 'rc-toc__link';
      link.textContent = heading.textContent.trim();
      li.appendChild(link);
      this.list.appendChild(li);
    });

    if (!this.headings.length) {
      this.hidden = true;
      return;
    }

    this.initSpy();
    this.initPlatzierung();
    // Mobil: Akkordeon default offen lassen, auf Desktop irrelevant (CSS)
  }

  /*
   * Desktop: Das Verzeichnis steht fest in der linken Randspalte. Feste
   * Positionierung kennt weder den Header noch das Seitenende — es schob sich
   * deshalb ueber den Footer und stiess an die Kopfzeile, sobald diese beim
   * Hochscrollen wieder ausfaehrt. Beides wird hier laufend nachgefuehrt:
   * Oberkante folgt der tatsaechlichen Unterkante der Kopfzeile, Hoehe endet
   * vor dem Footer, und wenn kein sinnvoller Platz mehr bleibt, blendet das
   * Verzeichnis aus (auch fuer Screenreader und Tastatur).
   */
  initPlatzierung() {
    this.panel = this.querySelector('.rc-toc__details');
    if (!this.panel) return;

    const ABSTAND = 16;
    const MIN_HOEHE = 140;

    const nachfuehren = () => {
      if (!window.matchMedia('(min-width: 1200px)').matches) {
        this.panel.style.removeProperty('top');
        this.panel.style.removeProperty('max-height');
        this.panel.classList.remove('is-verborgen');
        return;
      }

      // Die Kopfzeile besteht aus mehreren Section-Gruppen (Infoband, USP,
      // Hauptzeile), von denen beim Scrollen nur eine angeheftet bleibt.
      // Massgeblich ist die unterste Kante, die gerade noch im Bild ist.
      let kopfUnten = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((g) => {
        const unten = g.getBoundingClientRect().bottom;
        if (unten > kopfUnten) kopfUnten = unten;
      });
      const fuss = document.querySelector('.shopify-section-group-footer-group');
      const oben = Math.max(ABSTAND, kopfUnten + ABSTAND);
      const grenze = fuss ? Math.min(window.innerHeight, fuss.getBoundingClientRect().top) : window.innerHeight;
      const hoehe = grenze - oben - ABSTAND;

      this.panel.style.top = `${Math.round(oben)}px`;
      this.panel.style.maxHeight = `${Math.max(0, Math.round(hoehe))}px`;
      this.panel.classList.toggle('is-verborgen', hoehe < MIN_HOEHE);
    };

    // Die Kopfzeile faehrt beim Hochscrollen animiert wieder aus, und zwar
    // per transform — Groesse und Scroll-Position aendern sich dabei nicht
    // mehr, ein ResizeObserver bliebe also stumm und das Verzeichnis stuende
    // fuer den Rest der Animation unter der Kopfzeile. Deshalb nach jedem
    // Scrollen fuer die Dauer der Animation Bild fuer Bild nachfuehren.
    const NACHLAUF = 900;
    let bis = 0;
    let laeuft = false;
    const frame = () => {
      nachfuehren();
      if (performance.now() < bis) {
        requestAnimationFrame(frame);
      } else {
        laeuft = false;
      }
    };
    const anstossen = () => {
      bis = performance.now() + NACHLAUF;
      if (laeuft) return;
      laeuft = true;
      requestAnimationFrame(frame);
    };

    nachfuehren();
    this.nachfuehren = nachfuehren;
    window.addEventListener('scroll', anstossen, { passive: true });
    window.addEventListener('resize', anstossen, { passive: true });

    // Die Kopfzeile wechselt ihre Hoehe ueber die Klasse `rc-scrolled` am
    // <html>-Element — teils erst nach dem letzten Scroll-Ereignis. Der
    // Klassenwechsel ist das verlaessliche Signal, die Zeitschleife allein
    // waere ein Wettlauf.
    if ('MutationObserver' in window) {
      new MutationObserver(anstossen).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  }

  initSpy() {
    if (!('IntersectionObserver' in window)) return;
    const links = this.querySelectorAll('.rc-toc__link');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((link) => {
            link.classList.toggle(
              'is-active',
              link.getAttribute('href') === `#${entry.target.id}`
            );
          });
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    this.headings.forEach((h) => observer.observe(h));
  }
}

customElements.define('rc-article-toc', RcArticleToc);
